import { useCallback, useContext, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from 'react-query';
import { PersistContext } from '../context/persistContext';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../types/graph';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLogout } from './patient.hooks';
import { MED_G_ROUTES } from '../routes';
import { setFullReset } from '../reducers/graph';
import { selectQueryVariant } from '../selectors/version';
import { hardReload } from '../util/navigation';
import { isWebsiteDemoPractice } from '../util/demoPractice';
import {
  DEMO_LEAD_STORAGE_KEY,
  getDemoRequestId,
} from '../components/DemoLead/demoLeadStorage';

export interface ResetOptions {
  invitationId?: string;
  version?: string;
  variant?: string;
  clearStorage?: boolean;
  practiceSlug?: string;
  /**
   * Stay on the page — soft-navigate instead of reloading, whatever the reset
   * carries. For callers that use the reset as an in-page state wipe and keep
   * driving the app afterwards (the debug scenario runner), where a reload
   * would tear down the caller mid-run.
   */
  softNavigate?: boolean;
}

export const useResetQuestionnaire = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const questionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );
  const practiceSlug = useSelector(
    (state: RootState) => state.consultation.practice?.slug,
  );
  const professionalSlug = useSelector(
    (state: RootState) => state.consultation.professional?.slug,
  );
  const invitationSlug = useSelector(
    (state: RootState) => state.invitationSlug,
  );
  const mode = useSelector((state: RootState) => state.graph.mode);
  const queryVariant = useSelector(selectQueryVariant);
  const consultationVariant = useSelector(
    (state: RootState) => state.consultation.variant,
  );
  const previousQueryVariant = useSelector(
    (state: RootState) => state.previousQueryVariant,
  );
  const professionalVariant = useSelector(
    (state: RootState) =>
      state.consultation.professional?.defaultQuestionnaireVariant ?? undefined,
  );
  const persistor = useContext(PersistContext);
  const purge = useMemo(
    () => (persistor ? persistor.purge : () => null),
    [persistor],
  );
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const buildUrl = useCallback(
    (
      path: string,
      params: {
        version?: string;
        variant?: string;
        demoLead?: string;
      },
    ) => {
      const newSearchParams = new URLSearchParams();
      for (const [key, value] of searchParams.entries()) {
        newSearchParams.set(key, value);
      }
      if (params.version) newSearchParams.set('version', params.version);
      if (params.variant) newSearchParams.set('variant', params.variant);
      if (params.demoLead) newSearchParams.set('demo_lead', params.demoLead);
      const query = newSearchParams.toString();
      return query ? `${path}?${query}` : path;
    },
    [searchParams],
  );

  const onSuccess = useCallback(
    async (options?: ResetOptions) => {
      await purge();

      const {
        invitationId,
        version,
        variant,
        clearStorage,
        practiceSlug: optionsPracticeSlug,
        softNavigate,
      } = options || {};

      const effectivePracticeSlug = optionsPracticeSlug || practiceSlug;

      const effectiveVariant =
        variant ??
        queryVariant ??
        consultationVariant ??
        previousQueryVariant ??
        professionalVariant;

      const useInvitationUrl =
        !invitationId &&
        !optionsPracticeSlug &&
        !variant &&
        mode === 'INVITATION' &&
        invitationSlug;

      const useProfessionalUrl =
        !invitationId &&
        !optionsPracticeSlug &&
        (mode === 'PROFESSIONAL_LINK' || mode === 'INVITATION' || variant) &&
        professionalSlug;

      const basePath = useInvitationUrl
        ? `/invitation/${invitationSlug}`
        : useProfessionalUrl
        ? `/professional/${professionalSlug}`
        : effectivePracticeSlug
        ? `/${effectivePracticeSlug}`
        : MED_G_ROUTES.INTRODUCTION;

      const demoLead =
        basePath === `/${effectivePracticeSlug}` &&
        isWebsiteDemoPractice(effectivePracticeSlug)
          ? getDemoRequestId()
          : undefined;

      const url = buildUrl(basePath, {
        version,
        variant: effectiveVariant,
        demoLead,
      });

      if (clearStorage) {
        const savedDebugMode = sessionStorage.getItem('debugMode');
        const savedDisplayFullQueue =
          localStorage.getItem('DISPLAY_FULL_QUEUE');
        const savedDemoLead = sessionStorage.getItem(DEMO_LEAD_STORAGE_KEY);
        localStorage.clear();
        sessionStorage.clear();
        if (savedDebugMode) {
          sessionStorage.setItem('debugMode', savedDebugMode);
        }
        if (savedDisplayFullQueue) {
          localStorage.setItem('DISPLAY_FULL_QUEUE', savedDisplayFullQueue);
        }
        if (savedDemoLead) {
          sessionStorage.setItem(DEMO_LEAD_STORAGE_KEY, savedDemoLead);
        }
      }

      console.log('url', url);

      // Reload rather than soft-navigate whenever the destination URL carries
      // state the store only reads at creation time (`?version=`, `?variant=`),
      // or when storage was wiped underneath it. Keying this off the *resolved*
      // variant matters for callers that pass no options at all — the kiosk's
      // « Nouveau patient » button and its inactivity timeout — where the
      // variant comes from the state, not the arguments (ALDO-2598).
      !softNavigate &&
      (version || effectiveVariant || clearStorage || optionsPracticeSlug)
        ? hardReload(url)
        : navigate(url);
    },
    [
      navigate,
      purge,
      practiceSlug,
      professionalSlug,
      invitationSlug,
      mode,
      buildUrl,
      queryVariant,
      consultationVariant,
      previousQueryVariant,
      professionalVariant,
    ],
  );

  const { mutateAsync: logout } = useLogout();

  const resetQuestionnaire = useCallback(
    async (options?: ResetOptions) => {
      if (options?.clearStorage) {
        dispatch(setFullReset(true));
      }
      queryClient.invalidateQueries({ queryKey: 'patients' });
      queryClient.invalidateQueries({ queryKey: 'patient' });
      try {
        await logout();
      } catch {
        // Expected with no active session or a dead network — cleanup must
        // still proceed. Breadcrumb, not an event: during an outage this
        // would fire for every resetting patient.
        Sentry.addBreadcrumb({
          category: 'reset',
          message: 'Logout failed during questionnaire reset',
          level: 'warning',
        });
      }
      await onSuccess(options);
    },
    [dispatch, logout, questionnaireId, onSuccess, queryClient],
  );

  return resetQuestionnaire;
};
